import { Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import { delftBlue } from "@/theme/colors";
import CategoryBadge from "./CategoryBadge";

const ViewIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M19.7305 11.2265C19.9768 11.5715 20.0999 11.7449 20.0999 12C20.0999 12.256 19.9768 12.4285 19.7305 12.7736C18.6241 14.3255 15.798 17.67 11.9999 17.67C8.201 17.67 5.37572 14.3247 4.26926 12.7736C4.02302 12.4285 3.8999 12.2552 3.8999 12C3.8999 11.7441 4.02302 11.5715 4.26926 11.2265C5.37572 9.67451 8.20181 6.33002 11.9999 6.33002C15.7988 6.33002 18.6241 9.67532 19.7305 11.2265Z"
      stroke={delftBlue[500]}
      strokeWidth="1.215"
    />
    <path
      d="M14.4299 12C14.4299 11.3555 14.1739 10.7375 13.7182 10.2817C13.2625 9.82603 12.6444 9.57002 11.9999 9.57002C11.3554 9.57002 10.7373 9.82603 10.2816 10.2817C9.82592 10.7375 9.5699 11.3555 9.5699 12C9.5699 12.6445 9.82592 13.2626 10.2816 13.7183C10.7373 14.174 11.3554 14.43 11.9999 14.43C12.6444 14.43 13.2625 14.174 13.7182 13.7183C14.1739 13.2626 14.4299 12.6445 14.4299 12Z"
      stroke={delftBlue[500]}
      strokeWidth="1.215"
    />
  </svg>
);

const formDisplayTitle = (form, isSuperAdmin) => {
  if (isSuperAdmin) return form?.name || "Untitled form";
  return form?.versionTitle?.trim() || form?.activityId?.name || form?.name || "Untitled form";
};

const FormTable = ({ forms = [], onView, isSuperAdmin = false }) => {
  const theme = useTheme();

  const handleViewClick = (form) => {
    onView && onView(form);
  };

  return (
    <>
      <Box
        sx={{
          alignSelf: "stretch",
          position: "relative",
          overflowX: "auto",
          overflowY: "visible",
          borderRadius: 2,
          outline: `1px ${delftBlue[100]} solid`,
          outlineOffset: "-1px",
          width: "100%",
          "&::-webkit-scrollbar": {
            height: 8,
          },
          "&::-webkit-scrollbar-track": {
            background: delftBlue[50],
            borderRadius: 4,
          },
          "&::-webkit-scrollbar-thumb": {
            background: delftBlue[200],
            borderRadius: 4,
            "&:hover": {
              background: delftBlue[300],
            },
          },
        }}
      >
        <Table
          sx={{
            minWidth: { xs: 600, sm: "100%" },
            width: "100%",
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: delftBlue[500],
                "& .MuiTableCell-root": {
                  backgroundColor: `${delftBlue[500]} !important`,
                },
              }}
            >
              <TableCell
                sx={{
                  flex: "1 1 0",
                  height: { xs: 44, sm: 48 },
                  paddingLeft: { xs: 1.5, sm: 2 },
                  paddingRight: { xs: 1.5, sm: 2 },
                  paddingTop: { xs: 0.75, sm: 1 },
                  paddingBottom: { xs: 0.75, sm: 1 },
                  backgroundColor: `${delftBlue[500]} !important`,
                  borderRight: `1px ${theme.palette.neutral[200]} solid`,
                  borderBottom: `1px ${theme.palette.neutral[200]} solid`,
                  minWidth: { xs: 150, sm: "auto" },
                }}
              >
                <Typography
                  sx={{
                    color: "white",
                    fontSize: { xs: 12, sm: 13 },
                    fontFamily: "Audiowide, sans-serif",
                    fontWeight: 400,
                  }}
                >
                  Forms
                </Typography>
              </TableCell>
              <TableCell
                sx={{
                  flex: "1 1 0",
                  height: { xs: 44, sm: 48 },
                  paddingLeft: { xs: 1.5, sm: 2 },
                  paddingRight: { xs: 1.5, sm: 2 },
                  paddingTop: { xs: 0.75, sm: 1 },
                  paddingBottom: { xs: 0.75, sm: 1 },
                  backgroundColor: `${delftBlue[500]} !important`,
                  borderRight: `1px ${theme.palette.neutral[200]} solid`,
                  borderBottom: `1px ${theme.palette.neutral[200]} solid`,
                  minWidth: { xs: 150, sm: "auto" },
                }}
              >
                <Typography
                  sx={{
                    color: "white",
                    fontSize: { xs: 12, sm: 13 },
                    fontFamily: "Audiowide, sans-serif",
                    fontWeight: 400,
                  }}
                >
                  Activity Name
                </Typography>
              </TableCell>
              <TableCell
                sx={{
                  width: { xs: 90, sm: 110 },
                  height: { xs: 44, sm: 48 },
                  paddingLeft: { xs: 1.5, sm: 2 },
                  paddingRight: { xs: 1.5, sm: 2 },
                  paddingTop: { xs: 0.75, sm: 1 },
                  paddingBottom: { xs: 0.75, sm: 1 },
                  backgroundColor: `${delftBlue[500]} !important`,
                  borderRight: `1px ${theme.palette.neutral[200]} solid`,
                  borderBottom: `1px ${theme.palette.neutral[200]} solid`,
                }}
              >
                <Typography
                  sx={{
                    color: "white",
                    fontSize: { xs: 12, sm: 13 },
                    fontFamily: "Audiowide, sans-serif",
                    fontWeight: 400,
                  }}
                >
                  Version
                </Typography>
              </TableCell>
              <TableCell
                sx={{
                  width: { xs: 140, sm: 180 },
                  height: { xs: 44, sm: 48 },
                  paddingLeft: { xs: 1.5, sm: 2 },
                  paddingRight: { xs: 1.5, sm: 2 },
                  paddingTop: { xs: 0.75, sm: 1 },
                  paddingBottom: { xs: 0.75, sm: 1 },
                  backgroundColor: `${delftBlue[500]} !important`,
                  borderRight: `1px ${theme.palette.neutral[200]} solid`,
                  borderBottom: `1px ${theme.palette.neutral[200]} solid`,
                }}
              >
                <Typography
                  sx={{
                    color: "white",
                    fontSize: { xs: 12, sm: 13 },
                    fontFamily: "Audiowide, sans-serif",
                    fontWeight: 400,
                  }}
                >
                  Companies
                </Typography>
              </TableCell>
              <TableCell
                sx={{
                  width: { xs: 120, sm: 160 },
                  height: { xs: 44, sm: 48 },
                  paddingLeft: { xs: 1.5, sm: 2 },
                  paddingRight: { xs: 1.5, sm: 2 },
                  paddingTop: { xs: 0.75, sm: 1 },
                  paddingBottom: { xs: 0.75, sm: 1 },
                  backgroundColor: `${delftBlue[500]} !important`,
                  borderRight: `1px ${theme.palette.neutral[200]} solid`,
                  borderBottom: `1px ${theme.palette.neutral[200]} solid`,
                }}
              >
                <Typography
                  sx={{
                    color: "white",
                    fontSize: { xs: 12, sm: 13 },
                    fontFamily: "Audiowide, sans-serif",
                    fontWeight: 400,
                  }}
                >
                  Category
                </Typography>
              </TableCell>
              <TableCell
                sx={{
                  height: { xs: 44, sm: 48 },
                  paddingLeft: { xs: 1.5, sm: 2 },
                  paddingRight: { xs: 1.5, sm: 2 },
                  paddingTop: { xs: 0.75, sm: 1 },
                  paddingBottom: { xs: 0.75, sm: 1 },
                  backgroundColor: `${delftBlue[500]} !important`,
                  textAlign: { xs: "left", sm: "center" },
                  minWidth: { xs: 100, sm: "auto" },
                }}
              >
                <Typography
                  sx={{
                    color: "white",
                    fontSize: 13,
                    fontFamily: "Audiowide, sans-serif",
                    fontWeight: 400,
                  }}
                >
                  Actions
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {forms.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: "center", padding: 4 }}>
                  <Typography
                    sx={{
                      color: theme.palette.neutral[500],
                      fontSize: { xs: 14, sm: 15 },
                      fontFamily: "Montserrat, sans-serif",
                    }}
                  >
                    No forms found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              forms.map((form, index) => (
              <TableRow
                key={form.id || index}
                sx={{
                  height: { xs: 50, sm: 40 },
                  background: index % 2 === 0 ? "white" : delftBlue[50],
                  borderBottom: `1px ${theme.palette.neutral[200]} solid`,
                  "&:hover": {
                    background: theme.palette.neutral[50],
                  },
                }}
              >
                <TableCell
                  sx={{
                    flex: "1 1 0",
                    paddingLeft: { xs: 1.5, sm: 2 },
                    paddingRight: { xs: 1.5, sm: 2 },
                    borderRight: `1px ${theme.palette.neutral[200]} solid`,
                  }}
                >
                  <Typography
                    sx={{
                      color: theme.palette.neutral[700],
                      fontSize: { xs: 14, sm: 15, md: 16 },
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: 500,
                      wordBreak: "break-word",
                    }}
                  >
                    {formDisplayTitle(form, isSuperAdmin)}
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    flex: "1 1 0",
                    paddingLeft: { xs: 1.5, sm: 2 },
                    paddingRight: { xs: 1.5, sm: 2 },
                    borderRight: `1px ${theme.palette.neutral[200]} solid`,
                  }}
                >
                  <Typography
                    sx={{
                      color: theme.palette.neutral[700],
                      fontSize: { xs: 14, sm: 15, md: 16 },
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: 500,
                      wordBreak: "break-word",
                    }}
                  >
                    {form.activityId?.name || "N/A"}
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    width: { xs: 90, sm: 110 },
                    paddingLeft: { xs: 1.5, sm: 2 },
                    paddingRight: { xs: 1.5, sm: 2 },
                    borderRight: `1px ${theme.palette.neutral[200]} solid`,
                  }}
                >
                  <Chip
                    label={`v${form.templateVersion || form.latestVersion || 1}`}
                    size="small"
                    sx={{ fontFamily: "Montserrat, sans-serif" }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    width: { xs: 140, sm: 180 },
                    paddingLeft: { xs: 1.5, sm: 2 },
                    paddingRight: { xs: 1.5, sm: 2 },
                    borderRight: `1px ${theme.palette.neutral[200]} solid`,
                  }}
                >
                  <Typography
                    sx={{
                      color: theme.palette.neutral[700],
                      fontSize: { xs: 13, sm: 14 },
                      fontFamily: "Montserrat, sans-serif",
                    }}
                  >
                    {Array.isArray(form.companyIds) && form.companyIds.length
                      ? form.companyIds.length
                      : 0}
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    width: { xs: 120, sm: 160 },
                    paddingLeft: { xs: 1.5, sm: 2 },
                    paddingRight: { xs: 1.5, sm: 2 },
                    borderRight: `1px ${theme.palette.neutral[200]} solid`,
                  }}
                >
                  <CategoryBadge category="activity" />
                </TableCell>
                <TableCell
                  sx={{
                    paddingLeft: { xs: 1.5, sm: 2 },
                    paddingRight: { xs: 1.5, sm: 2 },
                    textAlign: { xs: "left", sm: "right" },
                  }}
                >
                  <Box
                    onClick={() => handleViewClick(form)}
                    sx={{
                      paddingLeft: { xs: 0.75, sm: 1 },
                      paddingRight: { xs: 0.75, sm: 1 },
                      paddingTop: { xs: 0.5, sm: 0.5 },
                      paddingBottom: { xs: 0.5, sm: 0.5 },
                      background: "white",
                      borderRadius: 2,
                      outline: `1px ${theme.palette.neutral[200]} solid`,
                      outlineOffset: "-1px",
                      display: "inline-flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 0.5,
                      cursor: "pointer",
                      touchAction: "manipulation",
                      minWidth: { xs: 60, sm: "auto" },
                      "&:hover": {
                        background: theme.palette.neutral[50],
                      },
                      "&:active": {
                        background: theme.palette.neutral[100],
                      },
                    }}
                  >
                    <ViewIcon />
                    <Typography
                      sx={{
                        color: delftBlue[500],
                        fontSize: { xs: 12, sm: 13 },
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                      }}
                    >
                      View
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>

    </>
  );
};

export default FormTable;

